import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import ArchiveIcon from '@mui/icons-material/Archive';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { newsApi } from '../services/api';
import { NewsPost } from '../types';

const NewsManage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<NewsPost | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['news-manage', statusFilter, search],
    queryFn: () => newsApi.getNewsPosts({
      status: statusFilter === 'all' ? undefined : statusFilter,
      search: search || undefined,
      limit: 100,
      include_hidden: true,  // Show all posts including archived/expired for management
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => newsApi.deleteNewsPost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-manage'] });
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    },
  });

  const publishMutation = useMutation({
    mutationFn: (id: number) => newsApi.publishNewsPost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-manage'] });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: number) => newsApi.archiveNewsPost(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news-manage'] });
    },
  });

  const handleDelete = (post: NewsPost) => {
    setPostToDelete(post);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (postToDelete) {
      deleteMutation.mutate(postToDelete.id);
    }
  };

  const getStatusColor = (status: string): 'default' | 'success' | 'warning' => {
    switch (status) {
      case 'published':
        return 'success';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 4 }}>
        <>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1">
            Manage News
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/news/create')}
          >
            Create News Post
          </Button>
        </Box>

        {/* Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <TextField
            label="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error">
            Failed to load news posts. Please try again.
          </Alert>
        )}

        {/* Table */}
        {!isLoading && !error && data && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell>Publish Date</TableCell>
                  <TableCell>Views</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.posts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">No news posts found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.posts.map((post) => (
                    <TableRow key={post.id}>
                      <TableCell>{post.title}</TableCell>
                      <TableCell>
                        <Chip label={post.status} color={getStatusColor(post.status)} size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={post.priority} size="small" />
                      </TableCell>
                      <TableCell>
                        {post.author.first_name} {post.author.last_name}
                      </TableCell>
                      <TableCell>
                        {post.publish_date ? new Date(post.publish_date).toLocaleDateString('en-US', { timeZone: 'America/New_York' }) : '-'}
                      </TableCell>
                      <TableCell>{post.views_count}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/news/edit/${post.id}`)}
                          title="Edit"
                        >
                          <EditIcon />
                        </IconButton>
                        {post.status === 'draft' && (
                          <IconButton
                            size="small"
                            onClick={() => publishMutation.mutate(post.id)}
                            title="Publish"
                            disabled={publishMutation.isLoading}
                          >
                            <PublishIcon />
                          </IconButton>
                        )}
                        {post.status === 'published' && (
                          <IconButton
                            size="small"
                            onClick={() => archiveMutation.mutate(post.id)}
                            title="Archive"
                            disabled={archiveMutation.isLoading}
                          >
                            <ArchiveIcon />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(post)}
                          title="Delete"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            Are you sure you want to delete "{postToDelete?.title}"? This action cannot be undone.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={confirmDelete}
              color="error"
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
        </>
      </Box>
    </Container>
  );
};

export default NewsManage;
